import { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface UrlParameterGuardProps {
  children: React.ReactNode;
}

/**
 * Global guard that checks URL parameters and store connections
 * Prevents users from bypassing onboarding by manipulating URL parameters
 */
export default function UrlParameterGuard({ children }: UrlParameterGuardProps) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, stores } = useAuth();

  useEffect(() => {
    const checkUrlParameters = async () => {
      // SIMPLIFIED APPROACH: Allow most access, only handle critical cases

      // Early return for allowed paths - no checks needed
      const currentPath = location.pathname;
      const allowedPaths = [
        '/profile', '/support', '/app/support', '/on-boarding', 
        '/signin', '/signup', '/forgot-password', '/reset-password',
        '/install', '/shopify-app', '/billing/choose-plan',
        '/admin', '/impersonate-login', '/email-template',
        '/invite', '/guest-store', '/alert'
      ];
      const isAllowedPath = allowedPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));
      
      if (isAllowedPath) {
        // console.log('UrlParameterGuard: Allowing access to allowed path:', currentPath);
        return;
      }
      
      // Only check for critical cases - shop parameter validation
      const shopParam = searchParams.get('shop');
      if (shopParam && user && stores.length > 0) {
        const specifiedStore = stores.find(store => store.shop === shopParam);
        
        // If shop parameter exists but store not found, check if user needs onboarding first
        if (!specifiedStore) {
          const needsOnboarding = Number(user?.skip) === 0;
          const hasInvitationToken = localStorage.getItem('invitation_token') || 
                                    sessionStorage.getItem('invitation_token') ||
                                    window.location.search.includes('invite');
          
          if (needsOnboarding && !hasInvitationToken) {
            // console.log('UrlParameterGuard: User needs onboarding, redirecting to /on-boarding');
            navigate('/on-boarding');
          } else {
            // console.log('UrlParameterGuard: Shop not found, redirecting to stores');
            navigate('/stores');
          }
          return;
        }
        
        // If shop exists but not connected, allow access (let page handle connection)
        // console.log('UrlParameterGuard: Shop found, allowing access');
        return;
      }
      
      // For all other cases, allow access and let pages handle their own permissions
      // console.log('UrlParameterGuard: Allowing access to:', currentPath);
    };

    checkUrlParameters();
  }, [searchParams, location.pathname, user?.id, stores.length, navigate]);

  return <>{children}</>;
}
